/**
 * Interior thresholds giving exactly `count` equal-width bins over the data
 * extent (the `count - 1` values strictly between min and max — matches d3
 * `bin().thresholds(...)`, which implicitly extends the outermost bins to the
 * data domain). Do NOT pass this directly to Observable Plot's `binX` as
 * `thresholds:` — Plot treats an explicit thresholds array as the COMPLETE
 * boundary list with no implicit domain extension, so the interior-only array
 * would silently drop both tail bins. Use {@link plotBinThresholds} for Plot.
 * Empty/degenerate (min===max) input → [] (single bin).
 */
export function binThresholds(values: readonly number[], count: number): number[] {
  if (values.length === 0 || count < 1) return [];
  let min = Infinity;
  let max = -Infinity;
  for (const v of values) {
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (min === max) return [];
  const width = (max - min) / count;
  const edges: number[] = [];
  for (let i = 1; i < count; i++) {
    edges.push(min + i * width);
  }
  return edges;
}

/**
 * Full boundary list (min, interior edges, max) for exactly `count`
 * equal-width bins — pass this to Observable Plot's `binX` as
 * `thresholds: plotBinThresholds(values, count)`. Unlike `binThresholds`
 * (interior edges only, which d3's own `bin()` implicitly extends to the data
 * domain), Plot's `binX` transform takes an explicit thresholds array as the
 * COMPLETE boundary list with no implicit extension: handed only the interior
 * edges, Plot would silently drop both tail bins and render `count - 2` bars
 * instead of `count`. `[]` for degenerate input (empty or min === max).
 */
export function plotBinThresholds(values: readonly number[], count: number): number[] {
  const interior = binThresholds(values, count);
  if (values.length === 0) return [];
  let min = values[0]!;
  let max = values[0]!;
  for (const v of values) {
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (min === max) return [];
  return [min, ...interior, max];
}

/**
 * Thin a bin-edge list for use as explicit Plot axis ticks. With up to 30
 * bins the full edge list (31 labels) collides at typical chart widths, so
 * above 12 edges this keeps every other one while always retaining the
 * final edge (so the last bin's upper bound stays labeled). 12 or fewer
 * edges pass through unchanged. Shared by the histogram and cumulative-
 * frequency plots (both axis on the same bin edges).
 */
export function axisTicks(edges: readonly number[]): number[] {
  if (edges.length <= 12) return [...edges];
  const thinned = edges.filter((_, i) => i % 2 === 0);
  const last = edges[edges.length - 1]!;
  if (thinned[thinned.length - 1] !== last) thinned.push(last);
  return thinned;
}
